import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import type {
  AuthEntriConfig,
  EntriContextProviderProps,
  EntriContextType,
  EntriEventDetail,
  PartialEntriConfig,
} from './EntriContext.types'
import { defaultContextValue } from './consts'
import { useGetAuthToken } from './hooks/useGetAuthToken'
import { useLoadEntri } from './hooks/useLoadEntri'

export const EntriContext = createContext<EntriContextType>(defaultContextValue)

export const EntriContextProvider = ({ children }: EntriContextProviderProps) => {
  const { getAuthToken } = useGetAuthToken()
  const { isEntriLoaded, loadEntriScript } = useLoadEntri()
  const [entriEnabled, setEntriEnabled] = useState(false)

  useEffect(() => {
    if (entriEnabled && !isEntriLoaded) {
      loadEntriScript()
      getAuthToken()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entriEnabled])

  const getAuthEntriConfig = useCallback(async (): Promise<AuthEntriConfig> => {
    const authToken = await getAuthToken()

    if (!authToken) {
      throw new Error('Invalid auth token')
    }

    const { applicationId, token } = authToken

    return { applicationId, token }
  }, [getAuthToken])

  const checkDomain = useCallback(
    async (domain: string, config: PartialEntriConfig) => {
      const authEntriConfig = await getAuthEntriConfig()

      return window.entri.checkDomain(domain, { ...config, ...authEntriConfig })
    },
    [getAuthEntriConfig],
  )

  const showEntriModal = useCallback(
    async (config: PartialEntriConfig) => {
      const authEntriConfig = await getAuthEntriConfig()

      return window.entri.showEntri({ ...config, ...authEntriConfig })
    },
    [getAuthEntriConfig],
  )

  const addEntriCloseEvent = (fn: (event: Event & EntriEventDetail) => void) => {
    // @ts-ignore
    window.addEventListener('onEntriClose', fn)
  }

  const removeEntriCloseEvent = (fn: (event: Event & EntriEventDetail) => void) => {
    // @ts-ignore
    window.removeEventListener('onEntriClose', fn)
  }

  const value: EntriContextType = useMemo(
    () => ({
      isEntriLoaded,
      enableEntri: () => setEntriEnabled(true),
      checkDomain,
      showEntriModal,
      addEntriCloseEvent,
      removeEntriCloseEvent,
    }),
    [isEntriLoaded, checkDomain, showEntriModal],
  )

  return <EntriContext.Provider value={value}>{children}</EntriContext.Provider>
}

export const useEntriContext = () => useContext(EntriContext)
